import React from "react";
import { Typography, Box, Grid, Collapse, IconButton } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import InsightsIcon from "@mui/icons-material/Insights";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TableChartIcon from "@mui/icons-material/TableChart";
import DashboardTableFilterModal from "../components/Dashboard/DashboardTableFilterModal";
import PIChart from "../components/Dashboard/PIChart";
import ProjectBarChart from "../components/Dashboard/ProjectBarChart";
import { RFItableColumns, RFItableFilters } from "../utils/constant";
import ProjectTable from "../components/Dashboard/ProjectTable";
import AppButton from "../components/Common/AppButton";
import theme from "../utils/theme";
import useDashboard from "../hooks/useDashboard";
import PaginationComponent from "../components/Common/PaginationComponent";

// Styles object
const styles = {
  rootContainer: {
    bgcolor: theme.palette.background.default,
    borderRadius: 2,
    boxShadow: "0 4px 12px rgba(44,62,80,0.14)",
  },
  chartTitle: {
    display: "flex",
    alignItems: "center",
    variant: "subtitle1",
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.text.light,
    borderRadius: "8px 8px 0 0",
    p: 1.2,
  },
  tableContainer: {
    mt: 3,
  },
  tableHeaderContainer: {
    display: "flex",
    alignItems: "center",
    mb: 2,
    justifyContent: "space-between",
  },
  tableTitleContainer: {
    display: "flex",
    alignItems: "center",
  },
  tableIcon: {
    mr: 1,
    color: theme.palette.text.primary,
    fontSize: 22,
    display: "flex",
    alignItems: "center",
  },
};

export default function Dashboard() {
  const {
    rfiList,
    pagination,
    showAnalytics,
    sortBy,
    sortDirection,
    open,
    fieldOptions,
    searchTerms,
    loadingStates,
    dateValues,
    loading,
    pieChartData,
    barChartData,
    assignLoader,
    handleAssignTemplate,
    handleSearchChange,
    handleFilterChange,
    getSelectedOptions,
    handleApplyFilters,
    handleResetFilters,
    openModal,
    closeModal,
    handleDateChange,
    handleClearDate,
    handlePageChange,
    setShowAnalytics,
    handleSort,
  } = useDashboard();
  // Filter modal state

  return (
    <Grid container>
      <Grid
        item
        container
        size={{ xs: 12 }}
        sx={styles.rootContainer}
        spacing={2}
      >
        <Grid item size={{ xs: 12 }} sx={styles.chartTitle} p={2}>
          <InsightsIcon sx={{ mr: 1 }} fontSize="small" />
          <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1 }}>
            <span>Quick Insights & Analytics</span>
          </Typography>
          <IconButton
            aria-label={
              showAnalytics ? "Collapse analytics" : "Expand analytics"
            }
            onClick={() => setShowAnalytics((prev) => !prev)}
            size="small"
            sx={{ ml: 1, color: theme.palette.text.light }}
          >
            {showAnalytics ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Grid>
        <Collapse
          in={showAnalytics}
          timeout="auto"
          unmountOnExit
          style={{ width: "100%" }}
        >
          <Grid container spacing={2}>
            <Grid item size={{ xs: 12, md: 4.5, lg: 3.5 }}>
              <PIChart data={pieChartData} />
            </Grid>
            <Grid item size={{ xs: 12, md: 7.5, lg: 8.5 }}>
              <ProjectBarChart data={barChartData} />
            </Grid>
          </Grid>
        </Collapse>
      </Grid>

      {/* Project Dashboard Records Table */}
      <Grid item size={{ xs: 12 }} sx={styles.tableContainer}>
        <Box sx={styles.tableHeaderContainer}>
          <Box sx={styles.tableTitleContainer}>
            <Box sx={styles.tableIcon}>
              <TableChartIcon
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: 26,
                  mr: 1,
                  verticalAlign: "middle",
                }}
              />
            </Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Project Dashboard Records
            </Typography>
          </Box>
          <AppButton
            label="Filter"
            buttonType="primary"
            startIcon={<FilterListIcon sx={{ fontSize: 22 }} />}
            onClick={openModal}
          />
        </Box>

        <DashboardTableFilterModal
          open={open}
          fieldOptions={fieldOptions}
          loadingStates={loadingStates}
          handleSearchChange={handleSearchChange}
          handleFilterChange={handleFilterChange}
          getSelectedOptions={getSelectedOptions}
          handleApplyFilters={handleApplyFilters}
          handleResetFilters={handleResetFilters}
          closeModal={closeModal}
          searchTerms={searchTerms}
          columns={RFItableFilters}
          dateValues={dateValues}
          handleDateChange={handleDateChange}
          handleClearDate={handleClearDate}
        />

        <ProjectTable
          tableData={rfiList}
          sortBy={sortBy}
          sortDirection={sortDirection}
          columns={RFItableColumns}
          showAnalytics={showAnalytics}
          handleSort={handleSort}
          loading={loading}
          handleAssignTemplate={handleAssignTemplate}
          assignLoader={assignLoader}
        />
        {pagination.total > pagination.perPage && (
          <PaginationComponent
            pagination={pagination}
            handlePageChange={handlePageChange}
          />
        )}
      </Grid>
    </Grid>
  );
}
